import { deriveKeyFromBaseAndSeed } from '../encrypt'

/**
 * Constants for AES-GCM
 */
const GCM_IV_LENGTH = 12 // 12 bytes for AES-GCM IV
const GCM_TAG_LENGTH = 16 // 16 bytes for AES-GCM authentication tag

/**
 * Remove prefix and suffix from obfuscated data
 *
 * @param data - Obfuscated data with prefix + data + suffix
 * @param prefix - Prefix to remove
 * @param suffix - Suffix to remove
 * @returns string - Data without prefix and suffix
 */
export const removeObfuscation = (data: string, prefix: string, suffix: string): string => {
  if (!data.startsWith(prefix) || !data.endsWith(suffix)) {
    throw new Error('Data does not contain the expected prefix or suffix')
  }
  return data.slice(prefix.length, -suffix.length)
}

/**
 * Convert a Base64 string to Uint8Array
 *
 * @param base64 - String encoded in Base64
 * @returns Uint8Array - Array of bytes
 */
export const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64)
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Decrypts data using AES-GCM
 * Expects: IV (12 bytes) || Ciphertext || Tag (16 bytes)
 *
 * @param key - Key of 32 bytes (Uint8Array)
 * @param encryptedData - IV + Ciphertext + Tag
 * @returns Promise<string> - Decrypted plaintext
 */
export const decryptGcm = async (key: Uint8Array, encryptedData: Uint8Array): Promise<string> => {
  try {
    if (key.length !== 32) {
      throw new Error('The key must have exactly 32 bytes (256 bits)')
    }

    if (encryptedData.length < GCM_IV_LENGTH + 1 + GCM_TAG_LENGTH) {
      throw new Error('Invalid encrypted data: insufficient size')
    }

    const iv = encryptedData.slice(0, GCM_IV_LENGTH)

    const ciphertextWithTag = encryptedData.slice(GCM_IV_LENGTH)

    const keyCopy = new Uint8Array(key)
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyCopy,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt'],
    )

    const plaintextBuffer = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      cryptoKey,
      ciphertextWithTag,
    )

    const decoder = new TextDecoder()
    return decoder.decode(plaintextBuffer)
  } catch (error) {
    throw new Error(
      `Error decrypting data: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

/**
 * Decrypt any encrypted string
 * @param seed - Seed used for encryption
 * @param encryptedString - The encrypted string with prefix and suffix
 * @returns Promise<string> - Decrypted string
 */
export const decryptString = async (seed: string, encryptedString: string): Promise<string> => {
  const dataWithoutObfuscation = removeObfuscation(encryptedString, 'bx_', '_xb')

  const encryptedData = base64ToUint8Array(dataWithoutObfuscation)

  const secretBaseKey = process.env.REACT_APP_SECRET_BASE_KEY
  const derivedKey = await deriveKeyFromBaseAndSeed(secretBaseKey ?? '', seed)

  const decryptedPayload = await decryptGcm(derivedKey, encryptedData)

  return decryptedPayload
}
